import moment from 'moment-timezone';
import { Link } from 'react-router-dom';
import { formatPhoneNumber } from './formatPhoneNumber';
import isInRole from './isInRole';
import { CASE_INITIATED_TYPES } from '../constants';

export const getMatterSource = (originator, source) => {
    const isClient = isInRole(originator?.roles || [], 'client');
    if (isClient) {
        return 'Client Self Book';
    }

    const isLawyer = isInRole(originator?.roles || [], 'lawyer');
    const isAgent = isInRole(originator?.roles || [], 'agent');
    if (isLawyer || isAgent) {
        return `${originator?.firstName || ''} ${
            !!originator?.lastName ? `${originator.lastName.charAt(0)}.` : ''
        }`;
    }

    return source?.displayName || source?.name || '';
};

export const displayPaymentMethod = (paymentMethod) => {
    if (!paymentMethod) {
        return '-';
    }

    return paymentMethod === 'CreditCard' ? 'Credit Card' : paymentMethod;
};

export const validateAppointmentDate = (
    lawyers,
    selectedDate,
    duration = 30,
) => {
    console.log('duration', duration);
    let isValid = false;
    for (let i = 0; i < lawyers.length; i++) {
        let lawyer = lawyers[i];
        if (lawyer.success) {
            let lawyerCalendar = lawyer.calendar[0];
            if (!!lawyerCalendar) {
                for (let k = 0; k < lawyerCalendar.schedule.length; k++) {
                    let selectedTime = moment(selectedDate).format('H:mm');

                    if (
                        moment(selectedTime, 'H:mm').isSameOrAfter(
                            moment(
                                lawyerCalendar.schedule[k].starthours,
                                'H:mm',
                            ),
                        ) &&
                        moment(selectedTime, 'H:mm')
                            .add(duration, 'minutes')
                            .isSameOrBefore(
                                moment(
                                    lawyerCalendar.schedule[k].endhours,
                                    'H:mm',
                                ),
                            )
                    ) {
                        isValid = true;
                        break;
                    }
                }
            }
        }
    }

    return isValid;
};

export const validateDescription = (description) => {
    return /^[\x09\x0A\x0D\x20-\x7E\xA0-\xFF]*$/.test(description);
};

export const validateLatin1 = (text) => {
    if (text === null || text === undefined || text === '') {
        return {
            valid: true,
            normalized: text ?? '',
            invalidChars: [],
        };
    }

    if (typeof text !== 'string') {
        return {
            valid: false,
            normalized: '',
            invalidChars: [
                {
                    char: text,
                    code: null,
                },
            ],
        };
    }

    // MySQL latin1 uses the Windows-1252 repertoire, not strict ISO-8859-1.
    const mysqlLatin1Extras = new Set([
        0x0081, 0x008d, 0x008f, 0x0090, 0x009d, 0x0152, 0x0153, 0x0160, 0x0161,
        0x0178, 0x017d, 0x017e, 0x0192, 0x02c6, 0x02dc, 0x2013, 0x2014, 0x2018,
        0x2019, 0x201a, 0x201c, 0x201d, 0x201e, 0x2020, 0x2021, 0x2022, 0x2026,
        0x2030, 0x2039, 0x203a, 0x20ac, 0x2122,
    ]);

    const invalidChars = [];

    for (const char of text) {
        const code = char.codePointAt(0);
        const isAscii = code >= 0x0000 && code <= 0x007f;
        const isLatin1Supplement = code >= 0x00a0 && code <= 0x00ff;

        if (!isAscii && !isLatin1Supplement && !mysqlLatin1Extras.has(code)) {
            invalidChars.push({
                char,
                code,
            });
        }
    }

    return {
        valid: invalidChars.length === 0,
        normalized: text,
        invalidChars,
    };
};

export const getAreaTitle = (referral) => {
    let title =
        referral?.lsrPracticeArea?.sourceLrsArea?.name ||
        referral?.lsrPracticeArea?.name ||
        '';
    if (!!referral?.lrsPracticeAreas && referral.lrsPracticeAreas.length > 0) {
        title += `, ${referral.lrsPracticeAreas
            .map((item) => item.name)
            .join(', ')}`;
    }

    if (!!referral?.legalIssues && referral.legalIssues.length > 0) {
        title += `, ${referral.legalIssues
            .map((item) => item.name)
            .join(', ')}`;
    }
    return title;
};

export const getDragged = (newList, oldList) => {
    const result = newList.filter(function (o1) {
        // filter out (!) items in result2
        return !oldList.some(function (o2) {
            return o1.id === o2.id; // assumes unique id
        });
    });

    return result[0];
};

export const getEcbaReferralStatus = (referral, asLink = false) => {
    if (referral.caseStatus === 'Uncompleted') {
        if (asLink) {
            return (
                <Link
                    to={`/agent_us_referrals?searchCriteria=${
                        referral.matterId
                    }${referral?.isClosed ? '&isClosed=true' : ''}`}
                >
                    {referral?.waitingStatus || ''}
                </Link>
            );
        }

        return referral?.waitingStatus || '';
    }

    if (referral.caseStatus === 'Pending') {
        if (asLink) {
            return (
                <Link
                    to={`/agent_us_referrals?searchCriteria=${
                        referral.matterId
                    }${referral?.isClosed ? '&isClosed=true' : ''}`}
                >
                    New
                </Link>
            );
        }
        return 'New';
    }

    if (referral.caseStatus === 'Cancelled') {
        if (asLink) {
            return (
                <Link
                    to={`/agent_us_referrals?searchCriteria=${
                        referral.matterId
                    }${referral?.isClosed ? '&isClosed=true' : ''}`}
                >
                    {`${referral.caseStatus}${
                        !!referral?.cancelledData ||
                        !!referral?.cancelledReferral
                            ? ` - ${
                                  referral?.cancelledData?.reason ||
                                  referral?.cancelledReferral?.reason ||
                                  ''
                              }`
                            : ''
                    }`}
                </Link>
            );
        }

        return `${referral.caseStatus}${
            !!referral.cancelledData || !!referral?.cancelledReferral
                ? ` - ${
                      referral?.cancelledData?.reason ||
                      referral?.cancelledReferral?.reason ||
                      ''
                  }`
                : ''
        }`;
    }

    if (referral.caseStatus === 'Declined') {
        if (asLink) {
            return (
                <Link
                    to={`/agent_us_referrals?searchCriteria=${
                        referral.matterId
                    }${referral?.isClosed ? '&isClosed=true' : ''}`}
                >
                    {`${referral.caseStatus}${
                        (referral?.referralDeclines || []).length > 0
                            ? ` - ${
                                  referral?.referralDeclines[0]?.declineReason
                                      ?.name || ''
                              }`
                            : ''
                    }`}
                </Link>
            );
        }

        return `${referral.caseStatus}${
            referral.referralDeclines.length > 0
                ? ` - ${
                      referral?.referralDeclines[0]?.declineReason?.name || ''
                  }`
                : ''
        }`;
    }

    if (referral.caseStatus === 'Complete') {
        if (asLink) {
            if (referral.isMatter) {
                return (
                    <Link
                        to={`/agent_us_matters?searchCriteria=${referral.matterId}`}
                    >
                        {referral.leadStatus === 'Hired'
                            ? 'Open'
                            : referral.leadStatus === 'Closed'
                            ? referral.leadStatusDescription
                            : referral.leadStatus}
                    </Link>
                );
            } else {
                return (
                    <Link
                        to={`/agent_us_leads?${
                            referral.leadStatus === 'Closed'
                                ? 'activeTab=2&'
                                : ''
                        }searchCriteria=${referral.matterId}`}
                    >
                        {referral.leadStatus === 'Hired'
                            ? 'Open'
                            : referral.leadStatus === 'Closed'
                            ? referral.leadStatusDescription
                            : referral.leadStatus}
                    </Link>
                );
            }
        }

        return referral.leadStatus === 'Hired'
            ? 'Open'
            : referral.leadStatus === 'Closed'
            ? referral.leadStatusDescription
            : referral.leadStatus;
    }
};

export const getEcbaReferralStage = (referral) => {
    if (referral.caseStatus === 'Uncompleted') {
        return 'Pending Referral';
    }

    if (
        referral.caseStatus === 'Pending' ||
        referral.caseStatus === 'Declined' ||
        referral.caseStatus === 'Cancelled'
    ) {
        return 'Referral';
    }

    if (
        referral.isMatter &&
        ['Hired', 'Closed', 'Hold'].includes(referral.leadStatus)
    ) {
        return 'Matter';
    }

    return 'Lead';
};

export const getLrsUserPrimaryPhone = (lrsUser) => {
    if (!lrsUser?.phoneNumber) {
        return '';
    }

    let phoneNumber = formatPhoneNumber(lrsUser.phoneNumber);
    if (lrsUser.isPrimaryPhoneMobile) {
        phoneNumber += ' - Mobile';
    }
    return phoneNumber;
};

export const getTransactionReferredBy = (user) => {
    const isClient = isInRole(user?.roles || [], 'client');
    if (isClient) {
        return 'Selfbook';
    }

    return `${user?.firstName || ''} ${user?.lastName || ''}`;
};

export const cleanObject = (obj) => {
    for (var propName in obj) {
        if (typeof obj[propName] === 'object') {
            cleanObject(obj[propName]);
        }
        if (!obj[propName]) {
            delete obj[propName];
        }
    }
    return obj;
};

export const isLFPSource = (sources) => {
    return (
        sources.findIndex((item) => {
            if (!!item.config) {
                let sourceConfig = JSON.parse(item.config);
                return sourceConfig.isLFP;
            }
            return false;
        }) > -1
    );
};

export const practiceAreasCrop = (referral, charsCount) => {
    let prArea = '';

    if (referral.practiceArea) {
        prArea +=
            referral?.practiceArea?.sourceArea?.name ||
            referral?.practiceArea?.name ||
            '';
    }
    if (referral?.legalIssues?.length > 0) {
        prArea += `${
            !!referral.practiceArea ? ' > ' : ''
        } ${referral.legalIssues
            .map((item) => item?.sourceArea?.name || item?.name)
            .join(', ')}`;
    }

    if (charsCount && prArea.length > charsCount) {
        prArea = prArea.substring(0, charsCount) + '...';
    }

    return prArea;
};

export const isInSource = (userSources, sourceId) => {
    // eslint-disable-next-line
    return userSources.findIndex((item) => item.id == sourceId) > -1;
};

export const referredBy = (referral, isCreatedData = false) => {
    const isLawyer = isInRole(referral?.originator?.roles || [], 'lawyer');
    const isAgent = isInRole(referral?.originator?.roles || [], 'agent');
    const isElcAgent = isInRole(referral?.originator?.roles || [], 'LRS Agent');
    if (isLawyer || isAgent || isElcAgent) {
        return `${referral?.originator?.firstName || ''} ${
            !!referral?.originator?.lastName
                ? `${referral?.originator.lastName.charAt(0)}.`
                : ''
        }`;
    }

    if (!!referral.waitingStatus) {
        if (!!isCreatedData) {
            return 'Web Form';
        }

        return '';
    }

    return 'Client Self-book';
};

export const referralCreatedBy = (referral) => {
    if (referral?.isReferralRequest) {
        return 'Referral Request';
    }

    if (referral.isAnonymous) {
        if (referral.initiatedType === CASE_INITIATED_TYPES.CLIENT_INTAKE) {
            return `Client Intake - ${
                referral?.source?.displayName || referral?.source?.name || ''
            }`;
        }

        if (!!referral.waitingStatus) {
            return 'Web Form';
        }

        return 'Client Self-book';
    }

    if (!referral.isAnonymous && !referral.createdBy) {
        return referral?.source?.name || '';
    }

    return `${referral?.createdBy?.firstName || ''} ${
        !!referral?.createdBy?.lastName
            ? `${referral?.createdBy.lastName.charAt(0)}.`
            : ''
    }`;
};

export const referralReferredBy = (referral) => {
    if (referral.caseStatus === 'Uncompleted') {
        return '';
    }

    if (referral.isAnonymous && !referral.waitingStatus) {
        if (referral.initiatedType === CASE_INITIATED_TYPES.CLIENT_INTAKE) {
            return `Client Intake - ${
                referral?.source?.displayName || referral?.source?.name || ''
            }`;
        }

        return 'Client Self-book';
    }

    if (
        (!referral.isAnonymous && !referral.referredBy && !!referral?.lawyer) ||
        (!!referral?.migrationDate && !referral?.lawyer)
    ) {
        return referral?.source?.name || '';
    }

    return `${referral?.referredBy?.firstName || ''} ${
        !!referral?.referredBy?.lastName
            ? `${referral?.referredBy.lastName.charAt(0)}.`
            : ''
    }`;
};

export const referralInitiatedBy = (referral) => {
    if (referral.caseStatus === 'Uncompleted') {
        return '';
    }

    if (referral.isAnonymous && !referral.waitingStatus) {
        return 'Client Self-book';
    }

    if (
        (!referral.isAnonymous &&
            !referral.initiatedBy &&
            !!referral?.lawyer) ||
        (!!referral?.migrationDate && !referral?.lawyer)
    ) {
        return referral?.source?.name || '';
    }

    return `${referral?.initiatedBy?.firstName || ''} ${
        !!referral?.initiatedBy?.lastName
            ? `${referral?.initiatedBy.lastName.charAt(0)}.`
            : ''
    }`;
};

export const formatAmount = (amount) => {
    let retValue = amount.toFixed(2);

    if (retValue.endsWith('.00')) {
        // Remove decimal places
        retValue = Math.floor(amount);
    }

    return retValue;
};

export const calculateForwardingFee = (amount, payload) => {
    console.log(amount);
    let forwardingFee = 0;

    if (
        payload.isComulative &&
        (amount > payload.freeAmount ||
            payload.totalAmount > payload.freeAmount)
    ) {
        payload.freeAmount = 0;
    }
    payload.fees.sort((a, b) => a.amount - b.amount);
    if (payload.isComulative) {
        payload.fees = payload.fees.filter((fee) => {
            return fee.amount > payload.totalAmount;
        });

        if (payload.fees.length) {
            payload.fees[0].amount =
                payload.fees[0].amount - payload.totalAmount;
        }
    }

    let remaining = amount - payload.freeAmount;

    // Apply fee for amounts up to max defined in the tiers
    for (const fee of payload.fees) {
        const applicableAmount = Math.min(
            remaining,
            fee.amount - payload.freeAmount,
        );
        forwardingFee += (applicableAmount * fee.percent) / 100;
        remaining -= applicableAmount;

        if (remaining <= 0) {
            break; // No need to iterate further if all fees are covered
        }
    }

    // Apply basePercent for the remaining amount over $15,000
    if (remaining > 0) {
        if (payload.isFlatFee) {
            forwardingFee = payload.flatFeeAmount;
        } else {
            forwardingFee += remaining * (payload.basePercent / 100);
        }
    }
    //}

    if (forwardingFee < 0) {
        forwardingFee = 0;
    }

    let qaseFeePercentAmount = Number(
        ((forwardingFee * payload.qaseFeePercent) / 100).toFixed(2),
    );

    let totalQaseFee = Number(
        (
            (forwardingFee * payload.qaseFeePercent) / 100 +
            payload.qaseFeePerTransactionAmount
        ).toFixed(2),
    );

    return {
        forwardingFee: Number(forwardingFee.toFixed(2)),
        qaseFeePercentAmount,
        totalQaseFee,
        qaseFeePerTransactionAmount: payload.qaseFeePerTransactionAmount,
        amount,
        totalAmount: payload.totalAmount,
    };
};

export const calculateWithTiers = (amount, payload) => {
    let total = 0;
    let previousLimit = 0;
    let percent = 0;
    const isCumulative = payload.isCumulative !== false; // default: true
    const cumulativeTotal = payload.previousTotalAmount + amount;

    let calculatedAmount = amount;

    if (payload.dueTreshold > 0) {
        const thresholdAlreadyUsed =
            payload.previousTotalAmount >= payload.dueTreshold;

        if (!thresholdAlreadyUsed) {
            if (cumulativeTotal <= payload.dueTreshold) {
                return returnZero(amount, cumulativeTotal, payload);
            }
        }

        if (payload.previousLrsAmount === 0) {
            calculatedAmount = cumulativeTotal;
        }
    }

    if (payload.exemptAmount > 0) {
        const exemptAlreadyUsed =
            payload.previousTotalAmount >= payload.exemptAmount;

        if (!exemptAlreadyUsed) {
            if (cumulativeTotal <= payload.exemptAmount) {
                return returnZero(amount, cumulativeTotal, payload);
            } else {
                calculatedAmount -=
                    payload.exemptAmount - payload.previousTotalAmount;
            }
        }
    }

    if (payload.isFlatFee) {
        total = payload.flatFeeAmount;
    } else {
        let applicableFees = [...payload.fees].sort(
            (a, b) => a.amount - b.amount,
        );

        if (!isCumulative) {
            applicableFees = applicableFees.filter(
                (fee) => fee.amount >= calculatedAmount,
            );
        }

        // Use feeable amounts for tier boundaries so split payments match
        // a single payment of the same cumulative total (exempt is cumulative).
        const baseStart = isCumulative
            ? payload.exemptAmount > 0
                ? Math.max(0, payload.previousTotalAmount - payload.exemptAmount)
                : payload.previousTotalAmount
            : 0;
        const baseEnd = isCumulative
            ? payload.exemptAmount > 0
                ? Math.max(0, cumulativeTotal - payload.exemptAmount)
                : payload.previousTotalAmount + calculatedAmount
            : calculatedAmount;

        for (let i = 0; i < applicableFees.length; i++) {
            const tier = applicableFees[i];
            const tierLimit = tier.amount;
            const tierPercent = tier.percent;

            const from = Math.max(baseStart, previousLimit);
            const to = Math.min(tierLimit, baseEnd);
            const tierAmount = to - from;

            if (tierAmount > 0) {
                total += tierAmount * (tierPercent / 100);
                previousLimit = tierLimit;
                percent = tierPercent;
            }
        }

        if (baseEnd > previousLimit) {
            const baseTierAmount = baseEnd - Math.max(previousLimit, baseStart);
            total += baseTierAmount * (payload.basePercent / 100);
            percent = payload.basePercent;
        }
    }

    // === 4. QASE FEES ===
    const qaseFeePercentAmount = Number(
        ((total * payload.qaseFeePercent) / 100).toFixed(2),
    );
    const totalQaseFee = Number(
        (qaseFeePercentAmount + payload.qaseFeePerTransactionAmount).toFixed(2),
    );

    return {
        forwardingFee: Number(total.toFixed(2)),
        qaseFeePercentAmount,
        totalQaseFee,
        qaseFeePerTransactionAmount: payload.qaseFeePerTransactionAmount,
        amount,
        totalAmount: cumulativeTotal,
        percent,
    };
};

const returnZero = (amount, totalAmount, payload) => ({
    forwardingFee: 0,
    qaseFeePercentAmount: 0,
    totalQaseFee: 0,
    qaseFeePerTransactionAmount: payload.qaseFeePerTransactionAmount,
    amount,
    totalAmount,
});

export const sleep = (delay) =>
    new Promise((resolve) => setTimeout(resolve, delay));

export const transformTree = (
    tree,
    propMapping,
    sortFn = null,
    disableArray = [],
) => {
    // Transform and sort the tree
    const transformedTree = tree.map((node) => {
        const transformedNode = {};

        // Map each property based on the propMapping
        for (const [newProp, oldProp] of Object.entries(propMapping)) {
            if (oldProp === 'children') {
                // Recursively transform and sort children
                transformedNode[newProp] = transformTree(
                    node[oldProp] || [],
                    propMapping,
                    sortFn,
                    disableArray,
                );
            } else {
                // Directly copy the property
                transformedNode[newProp] = node[oldProp];
            }
        }

        // Add disabled property if the node's value is in disableArray
        if (disableArray.includes(node[propMapping.value])) {
            transformedNode.disabled = true;
        }

        return transformedNode;
    });

    // Sort the transformed nodes if a sorting function is provided
    return sortFn ? transformedTree.sort(sortFn) : transformedTree;
};

export const sortBasePracticeAreaTreeDataFn = (a, b) => {
    const hasChildrenA = a.children && a.children.length > 0;
    const hasChildrenB = b.children && b.children.length > 0;

    if (hasChildrenA && !hasChildrenB) {
        return -1; // Panels with sub-panels come first
    }
    if (!hasChildrenA && hasChildrenB) {
        return 1; // Panels without sub-panels come later
    }

    // Sort alphabetically by name (or the mapped property)
    return a.title.localeCompare(b.title);
};

export const getFieldErrorNames = (formikErrors) => {
    const transformObjectToDotNotation = (obj, prefix = '', result = []) => {
        Object.keys(obj).forEach((key) => {
            const value = obj[key];
            if (!value) return;

            const nextKey = prefix ? `${prefix}.${key}` : key;
            if (typeof value === 'object') {
                transformObjectToDotNotation(value, nextKey, result);
            } else {
                result.push(nextKey);
            }
        });

        return result;
    };

    return transformObjectToDotNotation(formikErrors);
};

export const getFieldErrorMessages = (formikErrors) => {
    const transformObjectToDotNotation = (obj, prefix = '', result = []) => {
        Object.keys(obj).forEach((key) => {
            const value = obj[key];
            if (!value) return;

            const nextKey = prefix ? `${prefix}.${key}` : key;
            if (typeof value === 'object') {
                transformObjectToDotNotation(value, nextKey, result);
            } else {
                result.push(value);
            }
        });

        return result;
    };

    return transformObjectToDotNotation(formikErrors);
};

export const calculateMembershipSubscription = (membershipSubscription) => {
    let membersheepFee = parseFloat(membershipSubscription?.membershipFee) || 0;
    let panelFee = parseFloat(membershipSubscription?.panelFee) || 0;

    let memberShipTotal = membersheepFee + panelFee;
    let membersheepFeeDiscount = 0;
    if (!!membershipSubscription?.membershipDiscount) {
        if (membershipSubscription?.membershipDiscountType === 'fixed') {
            membersheepFeeDiscount =
                parseFloat(membershipSubscription?.membershipDiscount) || 0;
        } else {
            membersheepFeeDiscount =
                (membersheepFee *
                    parseFloat(membershipSubscription?.membershipDiscount) ||
                    0) / 100;
        }
    }

    let panelFeeDiscount = 0;
    if (!!membershipSubscription?.panelFeeDiscount) {
        if (membershipSubscription?.panelFeeDiscountType === 'fixed') {
            panelFeeDiscount =
                parseFloat(membershipSubscription?.panelFeeDiscount) || 0;
        } else {
            panelFeeDiscount =
                (panelFee *
                    parseFloat(membershipSubscription?.panelFeeDiscount) || 0) /
                100;
        }
    }

    let memberAndPanelDiscount = panelFeeDiscount + membersheepFeeDiscount;

    let additionalDiscount =
        parseFloat(membershipSubscription?.additionalDiscount) || 0;
    let totalDiscount =
        panelFeeDiscount + membersheepFeeDiscount + additionalDiscount;

    let donation = parseFloat(membershipSubscription?.donation) || 0;
    let total = memberShipTotal + donation;

    let prorateDiscount = 0;
    if (membershipSubscription?.prorate) {
        let totalAmount = total - totalDiscount;

        prorateDiscount = parseFloat(
            ((totalAmount / 12) * membershipSubscription.prorate).toFixed(2),
        );
        totalDiscount = totalDiscount + prorateDiscount;
    }

    return {
        membersheepFee,
        membersheepFeeDiscount,
        panelFee,
        panelFeeDiscount,
        memberAndPanelDiscount,
        additionalDiscount,
        prorateDiscount,
        donation,
        totalDiscount,
        memberShipTotal,
        total,
    };
};

export const formatPrice = (value) => {
    const num = Number(value);
    if (Number.isNaN(num)) return '';
    return num % 1 === 0 ? num.toString() : num.toFixed(2);
};

export const getTransactionType = (transaction) => {
    if (
        transaction?.charge_metadata?.transactionType ===
        'MEMBERSHIP_SUBSCRIPTION'
    ) {
        return 'Membership';
    }

    return (
        transaction?.charge_metadata?.transactionType || transaction?.type || ''
    );
};

export const getSubscriptionMembershipPeriod = (
    membershipStartDate,
    subscriptionCreated,
) => {
    const startDate = moment(membershipStartDate);
    const subscriptionDate = moment(subscriptionCreated);

    let currentPeriodStart = startDate.clone();

    while (
        subscriptionDate.isAfter(currentPeriodStart.clone().add(1, 'year'))
    ) {
        currentPeriodStart.add(1, 'year');
    }

    const currentPeriodEnd = currentPeriodStart.clone().add(1, 'year');

    return {
        start: currentPeriodStart,
        end: currentPeriodEnd,
    };
};

export const cropString = (value, maxLength) => {
    if (value.length <= maxLength) {
        return value;
    }

    return value.substring(0, maxLength) + '...';
};

export const decodeObjectFromUrlParam = (param) => {
    if (!param || typeof param !== 'string') return null;
    try {
        const base64 = decodeURIComponent(param);
        const json = atob(base64);
        return JSON.parse(json);
    } catch (error) {
        console.error('Invalid encoded object:', error);
        return null;
    }
};

export const buildFormData = (formData, data, parentKey) => {
    if (
        data &&
        typeof data === 'object' &&
        !(data instanceof Date) &&
        !(data instanceof File)
    ) {
        Object.keys(data).forEach((key) => {
            buildFormData(
                formData,
                data[key],
                parentKey ? `${parentKey}[${key}]` : key,
            );
        });
    } else {
        const value = data == null ? 'null' : data;
        formData.append(parentKey, value);
    }
};
